import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import './LedStripEditor.css';

/**
 * Editor de fitas LED baseado em âncoras no espaço 0–100 (viewBox).
 * - Clique no SVG para adicionar nós
 * - Arraste nós (clique no handle)
 * - ENTER: concluir (onCommit(d, anchors))
 * - ESC: cancelar (onCancel)
 * - DEL/BACKSPACE: remover último nó
 * - SHIFT: trava em 0°, 45°, 90° ao adicionar/arrastar
 *
 * Importante:
 * 1) Atalhos de teclado ficam DENTRO do editor (coeso).
 * 2) Expondo API imperativa via ref: commit(), cancel(), getState().
 * 3) Emite onStateChange({ canCommit }) quando âncoras mudam.
 */

const clamp01 = (v) => Math.max(0, Math.min(100, v));
const toD = (anchors) => {
  if (!anchors?.length) return '';
  const parts = anchors.map((p) => `${p.x.toFixed(2)} ${p.y.toFixed(2)}`);
  return parts.length === 1 ? `M ${parts[0]}` : `M ${parts[0]} L ${parts.slice(1).join(' ')}`;
};
const snap45 = (p0, p1) => {
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const a = Math.atan2(dy, dx);
  const step = Math.PI / 4;
  const snapped = Math.round(a / step) * step;
  const dist = Math.hypot(dx, dy);
  return { x: p0.x + Math.cos(snapped) * dist, y: p0.y + Math.sin(snapped) * dist };
};

const LedStripEditor = forwardRef(function LedStripEditor(
  {
    active,
    containerRef,
    color = '#f4bb00',
    strokeWidth = 2.5,
    initialAnchors = null,
    onCommit,
    onCancel,
    onStateChange, // pai pode habilitar/desabilitar "Concluir" do footer
  },
  ref
) {
  const [anchors, setAnchors] = useState(() => initialAnchors || []);
  const [hover, setHover] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [isShift, setIsShift] = useState(false);

  const overlayRef = useRef(null);

  // d path calculado a partir das âncoras
  const d = useMemo(() => toD(anchors), [anchors]);
  const canCommit = anchors.length >= 2;

  // Expor API imperativa para o pai (Footer/PlantCanvas)
  useImperativeHandle(
    ref,
    () => ({
      commit: () => {
        if (!active || !canCommit) return;
        onCommit?.(toD(anchors), anchors);
      },
      cancel: () => {
        if (!active) return;
        onCancel?.();
      },
      getState: () => ({ anchors, canCommit }),
    }),
    [active, anchors, canCommit, onCommit, onCancel]
  );

  // Inicializa anchors via prop quando mudar
  useEffect(() => {
    if (initialAnchors?.length) setAnchors(initialAnchors);
  }, [initialAnchors]);

  // Notifica o pai quando o estado de commit possível muda
  useEffect(() => {
    onStateChange?.({ canCommit });
  }, [canCommit, onStateChange]);

  // Atalhos de teclado do editor (ativos apenas quando active === true)
  useEffect(() => {
    if (!active) return;

    const onKeyDown = (e) => {
      if (e.key === 'Shift') setIsShift(true);

      if ((e.key === 'Backspace' || e.key === 'Delete') && anchors.length) {
        e.preventDefault();
        setAnchors((prev) => prev.slice(0, -1));
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel?.();
      }

      if (e.key === 'Enter' && canCommit) {
        e.preventDefault();
        onCommit?.(toD(anchors), anchors);
      }
    };

    const onKeyUp = (e) => {
      if (e.key === 'Shift') setIsShift(false);
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [active, anchors, canCommit, onCancel, onCommit]);

  const toPctPoint = useCallback(
    (evt) => {
      const el = containerRef?.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const x = clamp01(((evt.clientX - rect.left) / rect.width) * 100);
      const y = clamp01(((evt.clientY - rect.top) / rect.height) * 100);
      return { x, y };
    },
    [containerRef]
  );

  const onSvgMouseMove = useCallback(
    (e) => {
      if (!active) return;
      const p = toPctPoint(e);
      if (!p) return;

      if (dragIndex !== null) {
        setAnchors((prev) => {
          const next = [...prev];
          const base = dragIndex > 0 ? next[dragIndex - 1] : null;
          next[dragIndex] = isShift && base ? snap45(base, p) : p;
          return next;
        });
      } else {
        setHover(p);
      }
    },
    [active, dragIndex, isShift, toPctPoint]
  );

  const onSvgMouseDown = useCallback(
    (e) => {
      if (!active) return;
      if (e.button !== 0) return;

      // Impedir que clique no HUD/overlay adicione ponto por engano
      const overlay = overlayRef.current;
      if (overlay && overlay.contains(e.target) && !e.target.closest('svg')) {
        return;
      }

      const idxAttr = e.target?.getAttribute?.('data-handle-index');
      if (idxAttr !== null && idxAttr !== undefined) {
        setDragIndex(parseInt(idxAttr, 10));
        return;
      }

      const p = toPctPoint(e);
      if (!p) return;

      setAnchors((prev) => {
        const base = prev.length ? prev[prev.length - 1] : null;
        const snapped = isShift && base ? snap45(base, p) : p;
        return [...prev, snapped];
      });
    },
    [active, isShift, toPctPoint]
  );

  const onSvgMouseUp = useCallback(() => setDragIndex(null), []);

  if (!active) return null;

  return (
    <div
      className="ledstrip-editor-overlay"
      ref={overlayRef}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        className="ledstrip-editor-svg"
        onMouseMove={onSvgMouseMove}
        onMouseDown={onSvgMouseDown}
        onMouseUp={onSvgMouseUp}
      >
        {/* caminho consolidado */}
        {anchors.length >= 2 && (
          <>
            <path
              d={d}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
              opacity="0.95"
            />
            {/* hitbox para facilitar seleção/drag do caminho */}
            <path
              d={d}
              fill="none"
              stroke="rgba(0,0,0,0)"
              strokeWidth={Math.max(strokeWidth * 2, 6)}
              vectorEffect="non-scaling-stroke"
            />
          </>
        )}

        {/* preview do próximo segmento */}
        {anchors.length >= 1 && hover && dragIndex === null && (
          <path
            d={`M ${anchors[anchors.length - 1].x} ${anchors[anchors.length - 1].y} L ${hover.x} ${hover.y}`}
            fill="none"
            stroke={color}
            strokeDasharray="4 4"
            strokeWidth={strokeWidth}
            vectorEffect="non-scaling-stroke"
            opacity="0.8"
          />
        )}

        {/* handles */}
        {anchors.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={0.4}
            fill="#fff"
            stroke={color}
            strokeWidth={0.5}
            vectorEffect="non-scaling-stroke"
            data-handle-index={i}
            className="ledstrip-editor-handle"
          />
        ))}
      </svg>
    </div>
  );
});

export default LedStripEditor;